/**
 * Passport Sections Hooks
 *
 * CRUD hooks for certifications, experience, references, education, eligibility.
 *
 * IDENTITY CONTRACT (R37):
 *  - Passport tables are USER-OWNED (`user_id` FK to auth.users), NOT shop-owned.
 *  - These hooks MUST NOT take `activeShop`/`shopId`/`team_id`/`org_id` as input
 *    or read them from `useAuth()`. Passport works for users with zero, one, or
 *    many shop memberships, so anchoring to a shop is a category error.
 *  - Writes always set `user_id: <auth.uid()>`. Never `user_id: someoneElseId`.
 *
 * SECURITY NOTE (broad RLS — tighten in Phase 2):
 *  - passport_experience, passport_references, passport_education, and
 *    passport_eligibility currently have USING (true) SELECT policies. Any
 *    authenticated user can query anyone's data by user_id directly.
 *  - Until Phase 2 RLS lands, R37-CRIT-2 / R37-MED-4 mitigate by passing
 *    `isOwner` to the hook so non-owners get a column-narrowed payload (no
 *    phone/email/work-auth/background-check fields cross the wire).
 *  - The TalentProfileView UI is gated by the `get_talent_profile` RPC, so
 *    casual access is prevented, but a technically savvy user could query
 *    direct table reads. Phase 2 will replace USING (true) with policies
 *    checking `passport_visibility_settings.open_to_work_status`.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
// DTC-L: route Passport-sections `updated_at` lifecycle writes through
// the canonical clock for deterministic audit trail under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import type { Database } from '@/integrations/supabase/types';

// Row types for the dynamic (owner-vs-public) column selects below. postgrest-js
// can only infer a row shape from a STRING LITERAL select; these use a `columns`
// variable, so we type the result explicitly via `.returns<…>()` (no unsafe cast).
type ExperienceRow = Database['public']['Tables']['passport_experience']['Row'];
type ReferenceRow = Database['public']['Tables']['passport_references']['Row'];
type EducationRow = Database['public']['Tables']['passport_education']['Row'];
type EligibilityRow = Database['public']['Tables']['passport_eligibility']['Row'];

// ─── Certifications ───────────────────────────────────────────────────────────

export function usePassportCertifications(userId: string | undefined, options?: { visibleOnly?: boolean }) {
  return useQuery({
    queryKey: ['passport-certifications', userId, options?.visibleOnly],
    queryFn: async () => {
      let query = supabase
        .from('passport_certifications')
        .select('*')
        .eq('user_id', userId!)
        .order('created_at', { ascending: false });

      if (options?.visibleOnly) {
        query = query.eq('visibility', 'visible_to_shops');
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useCreateCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: {
      user_id: string;
      title: string;
      issuer?: string;
      issued_date?: string;
      expires_date?: string;
      credential_id?: string;
      notes?: string;
      visibility?: string;
      /** Core-100 R2 (CORE-L): a server-recorded document assessment to cite. */
      document_assessment_id?: string;
    }) => {
      const { data, error } = await supabase
        .from('passport_certifications')
        .insert(values as never)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['passport-certifications', data.user_id] });
      toast.success('Certification added');
    },
    onError: () => toast.error('Failed to add certification'),
  });
}

export function useUpdateCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...values }: {
      id: string;
      title?: string;
      issuer?: string;
      issued_date?: string | null;
      expires_date?: string | null;
      credential_id?: string | null;
      notes?: string | null;
      visibility?: string;
    }) => {
      const { data, error } = await supabase
        .from('passport_certifications')
        .update({ ...values, updated_at: clock.now().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['passport-certifications', data.user_id] });
      toast.success('Certification updated');
    },
    onError: () => toast.error('Failed to update certification'),
  });
}

export function useDeleteCertification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      // Core-100 R4 (Privacy Policy §6: information a User deletes in the App is deleted): the certification's files
      // are removed from storage too — before R4 the row was deleted and its uploaded files were orphaned in the
      // bucket. The paths are read first, the row is deleted (a server guard may refuse an attested credential; then
      // nothing is touched), and only then are the files removed; a storage failure is surfaced, never swallowed.
      const { data: media, error: mediaErr } = await supabase
        .from('passport_certification_media')
        .select('*')
        .eq('certification_id', id);
      if (mediaErr) throw mediaErr;
      const { error } = await supabase
        .from('passport_certifications')
        .delete()
        .eq('id', id);
      if (error) throw error;
      const paths = (media ?? []).map((m) => m.storage_path).filter((p): p is string => !!p);
      let filesRemoved = true;
      if (paths.length > 0) {
        const { error: storageErr } = await supabase.storage.from('passport-certifications').remove(paths);
        filesRemoved = !storageErr;
      }
      return { userId, filesRemoved };
    },
    onSuccess: ({ userId, filesRemoved }) => {
      qc.invalidateQueries({ queryKey: ['passport-certifications', userId] });
      if (filesRemoved) toast.success('Certification deleted');
      else toast.error('Certification deleted, but its files could not be removed. Please contact support.');
    },
    onError: () => toast.error('Failed to delete certification'),
  });
}

// ─── Certification Media ──────────────────────────────────────────────────────

export function useCertificationMedia(certId: string | undefined) {
  return useQuery({
    queryKey: ['certification-media', certId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('passport_certification_media')
        .select('*')
        .eq('certification_id', certId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!certId,
  });
}

export function useUploadCertificationMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, certId, userId }: { file: File; certId: string; userId: string }) => {
      const ext = file.name.split('.').pop() || 'bin';
      const path = `${userId}/${certId}/${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('passport-certifications')
        .upload(path, file);
      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase
        .from('passport_certification_media')
        .insert({
          certification_id: certId,
          user_id: userId,
          storage_path: path,
          mime_type: file.type,
          file_size: file.size,
        });
      if (dbError) throw dbError;

      // Update has_media on parent cert
      await supabase
        .from('passport_certifications')
        .update({ has_media: true } as any)
        .eq('id', certId);

      return { certId, userId };
    },
    onSuccess: ({ certId, userId }) => {
      qc.invalidateQueries({ queryKey: ['certification-media', certId] });
      qc.invalidateQueries({ queryKey: ['passport-certifications', userId] });
      toast.success('File uploaded');
    },
    onError: () => toast.error('Failed to upload file'),
  });
}

export function useDeleteCertificationMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, storagePath, certId, userId }: {
      id: string;
      storagePath: string;
      certId: string;
      userId: string;
    }) => {
      // Remove from storage
      await supabase.storage.from('passport-certifications').remove([storagePath]);
      // Delete DB row
      const { error } = await supabase
        .from('passport_certification_media')
        .delete()
        .eq('id', id);
      if (error) throw error;

      // Recompute has_media
      const { count } = await supabase
        .from('passport_certification_media')
        .select('id', { count: 'exact', head: true })
        .eq('certification_id', certId);

      await supabase
        .from('passport_certifications')
        .update({ has_media: (count ?? 0) > 0 } as any)
        .eq('id', certId);

      return { certId, userId };
    },
    onSuccess: ({ certId, userId }) => {
      qc.invalidateQueries({ queryKey: ['certification-media', certId] });
      qc.invalidateQueries({ queryKey: ['passport-certifications', userId] });
      toast.success('File removed');
    },
    onError: () => toast.error('Failed to remove file'),
  });
}

// ─── Experience (broad RLS -- tighten in Phase 2) ─────────────────────────────

// FIX-R41-MED-1: consistency with references/eligibility — accept an
// `isOwner` flag so non-owners get a column-narrowed projection. The
// experience schema doesn't carry phone/email PII today, but `description`
// + `notes` are free-text and may contain personal commentary the owner
// only intends for self-reference. The narrowed projection drops these
// while keeping the structured employment-history fields visible to
// shops/talent viewers.
// Core-100 R4 CORE-D: supersession and retraction history travel with the entry (what it once said stays visible).
// The free-text retraction reason is shown to the person and in-scope readers of the owner projection only.
// Typed `string`: the R4 history columns are not in the generated types, and parsing the literal is TS2590.
const EXPERIENCE_OWNER_COLUMNS: string =
  'id, user_id, employer_name, location, job_title, start_date, end_date, currently_here, description, created_at, updated_at, provenance_class, attested_by_shop_id, attested_shop_name, attested_at, attestation_withdrawn_at, supersedes_id, superseded_at, retracted_at, retraction_reason, retracted_attestation';
const EXPERIENCE_PUBLIC_COLUMNS: string =
  'id, user_id, employer_name, location, job_title, start_date, end_date, currently_here, created_at, updated_at, provenance_class, attested_by_shop_id, attested_shop_name, attested_at, attestation_withdrawn_at, supersedes_id, superseded_at, retracted_at, retracted_attestation';

export function usePassportExperience(
  userId: string | undefined,
  options?: { isOwner?: boolean }
) {
  const isOwner = !!options?.isOwner;
  return useQuery({
    queryKey: ['passport-experience', userId, isOwner ? 'owner' : 'public'],
    queryFn: async () => {
      const columns = isOwner ? EXPERIENCE_OWNER_COLUMNS : EXPERIENCE_PUBLIC_COLUMNS;
      const { data, error } = await supabase
        .from('passport_experience')
        .select(columns)
        .eq('user_id', userId!)
        .order('start_date', { ascending: false, nullsFirst: false })
        .returns<ExperienceRow[]>();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useCreateExperience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: {
      user_id: string;
      employer_name: string;
      location?: string;
      job_title?: string;
      start_date?: string;
      end_date?: string;
      currently_here?: boolean;
      description?: string;
    }) => {
      const { data, error } = await supabase
        .from('passport_experience')
        .insert(values)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['passport-experience', data.user_id] });
      toast.success('Experience added');
    },
    onError: () => toast.error('Failed to add experience'),
  });
}

export function useUpdateExperience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...values }: {
      id: string;
      employer_name?: string;
      location?: string | null;
      job_title?: string | null;
      start_date?: string | null;
      end_date?: string | null;
      currently_here?: boolean;
      description?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('passport_experience')
        .update({ ...values, updated_at: clock.now().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['passport-experience', data.user_id] });
      toast.success('Experience updated');
    },
    onError: () => toast.error('Failed to update experience'),
  });
}

export function useDeleteExperience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const { error } = await supabase.from('passport_experience').delete().eq('id', id);
      if (error) throw error;
      return { userId };
    },
    onSuccess: ({ userId }) => {
      qc.invalidateQueries({ queryKey: ['passport-experience', userId] });
      toast.success('Experience deleted');
    },
    onError: () => toast.error('Failed to delete experience'),
  });
}

// ─── References — read through the server authority ───────────────────────────
//
// SUPERSEDES the previous column-narrowing approach, which did not do what its
// comment claimed. It picked a narrow column list when `isOwner` was false — but
// `isOwner` is a PROP, and RLS is ROW-level, so nothing stopped a non-owner
// asking for the phone and email columns directly. Measured through a real
// authenticated client: a same-shop manager requested `phone, email, notes` and
// received all three, for a referee whose `share_contact_info` was FALSE.
//
// `share_contact_info` was likewise honoured only by the React component below,
// after the values had already crossed the wire.
//
// Both rules now live in `get_passport_references`, which also records the
// access in the same transaction as the disclosure. Direct SELECT on the
// sensitive columns is revoked at column level, so this is not merely the
// polite route — it is the only one.
export function usePassportReferences(
  userId: string | undefined,
  options?: { isOwner?: boolean }
) {
  // Retained for cache keying only. The SERVER decides what this viewer may
  // see; a caller can no longer influence it.
  const isOwner = !!options?.isOwner;
  return useQuery({
    queryKey: ['passport-references', userId, isOwner ? 'owner' : 'public'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_passport_references', { p_owner_user_id: userId! })
        .returns<ReferenceRow[]>();
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!userId,
  });
}

export function useCreateReference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: {
      user_id: string;
      name: string;
      relationship?: string;
      company?: string;
      phone?: string;
      email?: string;
      notes?: string;
      share_contact_info?: boolean;
    }) => {
      // Only the structural columns are returned. `.select()` would ask for
      // phone/email/notes, which clients no longer hold SELECT on — and a write
      // endpoint must not become a way to read back what the read path governs.
      const { data, error } = await supabase
        .from('passport_references')
        .insert(values)
        .select('id, user_id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['passport-references', data.user_id] });
      toast.success('Reference added');
    },
    onError: () => toast.error('Failed to add reference'),
  });
}

export function useUpdateReference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...values }: {
      id: string;
      name?: string;
      relationship?: string | null;
      company?: string | null;
      phone?: string | null;
      email?: string | null;
      notes?: string | null;
      share_contact_info?: boolean;
    }) => {
      // Structural columns only, for the same reason as the insert above.
      const { data, error } = await supabase
        .from('passport_references')
        .update({ ...values, updated_at: clock.now().toISOString() })
        .eq('id', id)
        .select('id, user_id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['passport-references', data.user_id] });
      toast.success('Reference updated');
    },
    onError: () => toast.error('Failed to update reference'),
  });
}

export function useDeleteReference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const { error } = await supabase.from('passport_references').delete().eq('id', id);
      if (error) throw error;
      return { userId };
    },
    onSuccess: ({ userId }) => {
      qc.invalidateQueries({ queryKey: ['passport-references', userId] });
      toast.success('Reference deleted');
    },
    onError: () => toast.error('Failed to delete reference'),
  });
}

// ─── Education (broad RLS -- tighten in Phase 2) ──────────────────────────────

// FIX-R41-MED-1: consistency with references/eligibility/experience.
// Education schema is structurally similar — `notes` is free-text and
// may contain personal/identifying commentary. Public projection drops
// notes while keeping the formal credentialing fields.
const EDUCATION_OWNER_COLUMNS =
  'id, user_id, school_name, program, degree, start_date, end_date, graduated, notes, created_at, updated_at, education_kind, attested_by_verification_id';
const EDUCATION_PUBLIC_COLUMNS =
  'id, user_id, school_name, program, degree, start_date, end_date, graduated, created_at, updated_at, education_kind, attested_by_verification_id';

export function usePassportEducation(
  userId: string | undefined,
  options?: { isOwner?: boolean }
) {
  const isOwner = !!options?.isOwner;
  return useQuery({
    queryKey: ['passport-education', userId, isOwner ? 'owner' : 'public'],
    queryFn: async () => {
      const columns = isOwner ? EDUCATION_OWNER_COLUMNS : EDUCATION_PUBLIC_COLUMNS;
      const { data, error } = await supabase
        .from('passport_education')
        .select(columns)
        .eq('user_id', userId!)
        .order('start_date', { ascending: false, nullsFirst: false })
        .returns<EducationRow[]>();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useCreateEducation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: {
      user_id: string;
      school_name: string;
      program?: string;
      degree?: string;
      start_date?: string;
      end_date?: string;
      graduated?: boolean;
      notes?: string;
      education_kind?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('passport_education')
        .insert(values)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['passport-education', data.user_id] });
      toast.success('Education added');
    },
    onError: () => toast.error('Failed to add education'),
  });
}

export function useUpdateEducation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...values }: {
      id: string;
      school_name?: string;
      program?: string | null;
      degree?: string | null;
      start_date?: string | null;
      end_date?: string | null;
      graduated?: boolean;
      notes?: string | null;
      education_kind?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('passport_education')
        .update({ ...values, updated_at: clock.now().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['passport-education', data.user_id] });
      toast.success('Education updated');
    },
    onError: () => toast.error('Failed to update education'),
  });
}

export function useDeleteEducation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const { error } = await supabase.from('passport_education').delete().eq('id', id);
      if (error) throw error;
      return { userId };
    },
    onSuccess: ({ userId }) => {
      qc.invalidateQueries({ queryKey: ['passport-education', userId] });
      toast.success('Education deleted');
    },
    onError: () => toast.error('Failed to delete education'),
  });
}

// ─── Eligibility (broad RLS -- tighten in Phase 2) ────────────────────────────

// FIX-R37-MED-4: column-narrowing for non-owners. Eligibility holds work
// authorization, background-check willingness, and drug-screen consent —
// privacy-sensitive even before the Phase 2 RLS tightening. Until the SELECT
// policy gates on visibility settings, only the owner gets the full row.
const ELIGIBILITY_OWNER_COLUMNS =
  'user_id, has_valid_drivers_license, authorized_to_work_us, available_start_date, willing_background_check, willing_drug_screen, willing_relocate, created_at, updated_at';
const ELIGIBILITY_PUBLIC_COLUMNS =
  'user_id, available_start_date, willing_relocate, created_at, updated_at';

export function usePassportEligibility(
  userId: string | undefined,
  options?: { isOwner?: boolean }
) {
  const isOwner = !!options?.isOwner;
  return useQuery({
    queryKey: ['passport-eligibility', userId, isOwner ? 'owner' : 'public'],
    queryFn: async () => {
      const columns = isOwner ? ELIGIBILITY_OWNER_COLUMNS : ELIGIBILITY_PUBLIC_COLUMNS;
      const { data, error } = await supabase
        .from('passport_eligibility')
        .select(columns)
        .eq('user_id', userId!)
        .maybeSingle()
        .returns<EligibilityRow>();
      if (error) throw error;
      return data; // null if no row yet
    },
    enabled: !!userId,
  });
}

export function useUpsertEligibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (values: {
      user_id: string;
      has_valid_drivers_license?: boolean | null;
      authorized_to_work_us?: boolean | null;
      available_start_date?: string | null;
      willing_background_check?: boolean | null;
      willing_drug_screen?: boolean | null;
      willing_relocate?: boolean | null;
    }) => {
      const { data, error } = await supabase
        .from('passport_eligibility')
        .upsert(
          { ...values, updated_at: clock.now().toISOString() },
          { onConflict: 'user_id' }
        )
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['passport-eligibility', data.user_id] });
      toast.success('Eligibility saved');
    },
    onError: () => toast.error('Failed to save eligibility'),
  });
}
